import { Component, OnInit } from '@angular/core';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { ResourceListFilterValuesType } from './partials/resources-filters/resources-filters.component';
import { faSlidersH } from '@fortawesome/pro-light-svg-icons';
import { SelectItem } from 'primeng/api';
import { HelperService } from 'projects/admin/src/app/services/helper.service';
import { TranslocoService } from '@ngneat/transloco';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import moment from 'moment-timezone';
import { DateRange, extendMoment } from 'moment-range';
import { AppointmentDataDaytype } from 'projects/core/src/lib/models/appointment.model';

const { range } = extendMoment(moment);
export const ROLES = ['moderator', 'moderator_external', 'observer', 'observer_gem'];
const MIN_DURATION = 120;

interface TimePeriod {
  starts_at: string;
  ends_at: string;
}

interface UserListEntryType {
  role: string;
  name: string;
  locations: string;
  templates: string;
  languages: string;
  type: string;
  blocked: any;
  availabilities: any;
  date?: string; // for export only
}

export interface DayListEntryType {
  date: string;
  moderator: number;
  moderator_external: number;
  observer: number;
  observer_gem: number;
  users?: Array<UserListEntryType>;
}

@Component({
  selector: 'app-resources-list',
  templateUrl: './resources-list.component.html',
  styleUrls: ['./resources-list.component.scss']
})
export class ResourcesListComponent implements OnInit {

  public loading = false;
  public set = false;
  public data: Array<DayListEntryType> = [];
  public options: { [key: string]: Array<SelectItem> };
  public filterValues: ResourceListFilterValuesType | null = null;

  public faSlidersH = faSlidersH;

  constructor(
    private api: ApiService,
    private helper: HelperService,
    private transloco: TranslocoService,
    private ui: UiService
  ) {
  }

  ngOnInit(): void {
    this.api.getResourcesFilters().subscribe({
      next: (result: any) => {
        this.options = result.data;
        this.options.roles = this.options.roles?.map((roleData: any) => {
          roleData.label = this.transloco.translate('common.labels.' + roleData.name);
          return roleData
        });
      }
    });
  }

  onFilterSet(filterValues: ResourceListFilterValuesType | null): void {
    this.filterValues = filterValues;
    if (this.filterValues !== null) {
      this.loading = true;
      this.set = true;
      this.api.getResourcesData(this.filterValues).subscribe({
        next: (result: any) => {
          this.processData(result?.data);
        }
      }).add(() => {
        this.loading = false;
      });
    } else {
      this.set = false;
      this.data = [];
    }
  }

  onExport(): void {
    const exportData: Array<any> = [];
    this.data.forEach((entry: DayListEntryType) => {
      entry.users?.forEach((user: UserListEntryType) => {
        exportData.push({
          [this.transloco.translate('common.labels.date')]: moment(entry.date).toDate(),
          [this.transloco.translate('common.labels.role')]: this.transloco.translate('common.labels.' + user.role),
          [this.transloco.translate('common.labels.name')]: user.name,
          [this.transloco.translate('common.labels.locations')]: user.locations,
          [this.transloco.translate('common.labels.templates')]: user.templates,
          [this.transloco.translate('common.labels.languages')]: user.languages,
        });
      });
    });
    if (exportData.length === 0) {
      this.ui.toastInfo(this.transloco.translate(this.transloco.translate('reportings_date_filter.messages.no_results')));
    } else {
      import('xlsx').then(xlsx => {
          const worksheet = xlsx.utils.json_to_sheet(exportData);
          const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
          const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
          this.helper.saveAsExcelFile(excelBuffer, 'report_resources');
      });
    }
  }

  private processData(data: Array<any>): void {
    const template: any = this.options?.['templates']?.find((entry: any) => entry.id === this.filterValues?.template) || null;
    const days: { [key: string]: DayListEntryType } = {};
    data.forEach((userData: any) => {
      const name = userData.firstname + ' ' + userData.lastname;
      const roles = this.options?.roles?.filter((role: any) => {
        return userData.roles.includes(role.id);
      });
      const locations = this.concatData(userData, 'locations', 'name');
      const templates = this.concatData(userData, 'templates', 'name');
      const languages = this.concatData(userData, 'languages', 'name');
      if (locations !== null && templates !== null && languages !== null) {
        userData.appointments.forEach((appointment: any) => {
          // set timings (day_start/day_end sind als CET-Zeiten gemeint)
          let start = null;
          let end = null;
          const timings = {
            start: moment.tz(appointment.day, 'Europe/Berlin').set({ hour: parseInt(this.options.timing['day_start'], 10), minute: 0, second: 0, millisecond: 0 }),
            center: moment.tz(appointment.day, 'Europe/Berlin').set({ hour: parseInt(this.options.timing['day_center'], 10), minute: parseInt(this.options.timing['day_center_minutes'], 10), second: 0, millisecond: 0 }),
            end: moment.tz(appointment.day, 'Europe/Berlin').set({ hour: parseInt(this.options.timing['day_end'], 10), minute: 0, second: 0, millisecond: 0 }),
          };
          if (appointment.data?.['type'] === AppointmentDataDaytype.Morning) {
            start = timings.start;
            end = timings.center;
          } else if (appointment.data?.['type'] === AppointmentDataDaytype.Afternoon) {
            start = timings.center;
            end = timings.end;
          } else {
            start = timings.start;
            end = timings.end;
          }
          let freeRanges: Array<DateRange> = [];
          const overallRange = range(start, end);
          if (Array.isArray(appointment.blocked) && appointment.blocked.length > 0) {
            const blockedRanges = appointment.blocked
              .map((period: TimePeriod) => range(moment.utc(period.starts_at), moment.utc(period.ends_at)))
              .sort((a: DateRange, b: DateRange) => a.start.valueOf() - b.start.valueOf());
            let currentStart = start;
            blockedRanges.forEach((blockedRange: DateRange) => {
              if (currentStart.isBefore(blockedRange.start)) {
                freeRanges.push(range(currentStart, blockedRange.start));
              }
              currentStart = blockedRange.end;
            });
            if (currentStart.isBefore(end)) {
              freeRanges.push(range(currentStart, end));
            }
          } else {
            freeRanges.push(overallRange);
          }
          const duration = template?.duration || MIN_DURATION;
          freeRanges = freeRanges.filter((freeRange: DateRange) => {
            return freeRange.end.diff(freeRange.start, 'minutes') >= duration;
          });
          // add data
          if (freeRanges.length > 0) {
            // init day
            if (!days[appointment.day]) {
              days[appointment.day] = {
                date: appointment.day,
                moderator: 0,
                moderator_external: 0,
                observer: 0,
                observer_gem: 0,
                users: [],
              };
            }
            // set data for user
            roles.forEach((role: any) => {
              if (ROLES.includes(role.name) && (this.filterValues?.roles === null || this.filterValues?.roles?.includes(role.id))) {
                if (days[appointment.day][role.name] !== undefined) {
                  days[appointment.day][role.name] += 1;
                }
                let dayLocations = null;
                if (Array.isArray(appointment.data?.['locations']) && appointment.data?.['locations'].length > 0) {
                  dayLocations = this.concatData(appointment.data, 'locations', 'name');
                }
                days[appointment.day].users.push({
                  role: role.name,
                  name,
                  locations: dayLocations || locations,
                  templates,
                  languages,
                  type: appointment.data?.['type'] || null,
                  blocked: appointment.blocked,
                  availabilities: freeRanges
                });
              }
            });
          }
        });
      }
    });
    this.data = Object.values(days).sort((a: DayListEntryType, b: DayListEntryType) => {
      return this.helper.getSortValue(a.date, b.date);
    });
  }

  private concatData(userData: any, mode: string, name: string): string | null {
    if (Array.isArray(userData[mode]) && Array.isArray(this.options[mode])) {
      const values = this.options[mode].filter((entry: any) => {
        return userData[mode].includes(entry.id);
      }).map((entry: any) => {
        return entry[name];
      });
      return (values?.length > 0) ? values.join(', ') : null;
    }
    return null;
  }

}
